import type { FloydSteinbergParams, FloydSteinbergParamsC } from '../types';

const W_RIGHT = 7 / 16;
const W_BOTTOM_LEFT = 3 / 16;
const W_BOTTOM = 5 / 16;
const W_BOTTOM_RIGHT = 1 / 16;

/**
 * Floyd-Steinberg error diffusion on a grayscale buffer.
 * 7/16 right, 3/16 bottom-left, 5/16 bottom, 1/16 bottom-right.
 * Serpentine alternates scan direction per row to suppress directional artifacts.
 * Strength scales the diffused error (1 = canonical, <1 = softer).
 */
export function floydSteinbergMask(
  source: ImageData,
  params: FloydSteinbergParams | FloydSteinbergParamsC,
): Uint8Array {
  const { density, serpentine, strength } = params;
  const w = source.width;
  const h = source.height;
  const buffer = new Float32Array(w * h);
  const densityShift = (50 - density) / 50;
  const src = source.data;

  for (let i = 0; i < w * h; i++) {
    buffer[i] = (src[i * 4] ?? 0) / 255 + densityShift;
  }

  const mask = new Uint8Array(w * h);

  for (let y = 0; y < h; y++) {
    const reverse = serpentine && y % 2 === 1;
    const startX = reverse ? w - 1 : 0;
    const endX = reverse ? -1 : w;
    const stepX = reverse ? -1 : 1;
    const dirSign = reverse ? -1 : 1;

    for (let x = startX; x !== endX; x += stepX) {
      const i = y * w + x;
      const oldVal = buffer[i] ?? 0;
      const newVal = oldVal > 0.5 ? 1 : 0;
      mask[i] = newVal;
      const err = (oldVal - newVal) * strength;

      const xRight = x + dirSign;
      if (xRight >= 0 && xRight < w) {
        const jR = i + dirSign;
        buffer[jR] = buffer[jR]! + err * W_RIGHT;
      }
      if (y + 1 < h) {
        const yi = (y + 1) * w;
        const xBL = x - dirSign;
        const xBR = x + dirSign;
        if (xBL >= 0 && xBL < w) {
          const jBL = yi + xBL;
          buffer[jBL] = buffer[jBL]! + err * W_BOTTOM_LEFT;
        }
        const jB = yi + x;
        buffer[jB] = buffer[jB]! + err * W_BOTTOM;
        if (xBR >= 0 && xBR < w) {
          const jBR = yi + xBR;
          buffer[jBR] = buffer[jBR]! + err * W_BOTTOM_RIGHT;
        }
      }
    }
  }

  return mask;
}
